
import { AuthError } from '@supabase/supabase-js';
import { PostgrestError } from '@supabase/postgrest-js';

export interface SupabaseErrorTranslator {
  translateSupabaseError: (error: AuthError | PostgrestError | any, t: (key: string) => string) => string;
}

// Common Supabase error patterns and their translation keys
const ERROR_MAPPINGS: Record<string, string> = {
  // Authentication errors
  'Invalid login credentials': 'auth.supabase.invalidCredentials',
  'Email not confirmed': 'auth.supabase.emailNotConfirmed',
  'User not found': 'auth.supabase.userNotFound',
  'Password should be at least 6 characters': 'auth.supabase.passwordTooShort',
  'User already registered': 'auth.supabase.userAlreadyRegistered',
  'Email rate limit exceeded': 'auth.supabase.emailRateLimit',
  'Invalid email': 'auth.supabase.invalidEmail',
  'Signup is disabled': 'auth.supabase.signupDisabled',
  'Invalid recovery token': 'auth.supabase.invalidRecoveryToken',
  'Token has expired or is invalid': 'auth.supabase.tokenExpired',
  'Password is too weak': 'auth.supabase.passwordTooWeak',
  
  // Database errors
  'Database error querying schema': 'auth.supabase.databaseError',
  'Connection timeout': 'auth.supabase.connectionTimeout',
  'Internal server error': 'auth.supabase.internalError',
  'Service unavailable': 'auth.supabase.serviceUnavailable',
  'Request timeout': 'auth.supabase.requestTimeout',
  
  // Policy/Permission errors
  'policy': 'auth.supabase.permissionDenied',
  'permission denied': 'auth.supabase.permissionDenied',
  'access denied': 'auth.supabase.accessDenied',
  'insufficient permissions': 'auth.supabase.insufficientPermissions',
  
  // Network errors
  'fetch': 'auth.supabase.networkError',
  'network': 'auth.supabase.networkError',
  'Failed to fetch': 'auth.supabase.networkError',
};

// Regex patterns for dynamic error messages
const ERROR_PATTERNS: Array<{ pattern: RegExp; translationKey: string }> = [
  { pattern: /rate limit/i, translationKey: 'auth.supabase.rateLimit' },
  { pattern: /timeout/i, translationKey: 'auth.supabase.connectionTimeout' },
  { pattern: /network/i, translationKey: 'auth.supabase.networkError' },
  { pattern: /connection/i, translationKey: 'auth.supabase.connectionError' },
  { pattern: /invalid.*token/i, translationKey: 'auth.supabase.invalidToken' },
  { pattern: /expired/i, translationKey: 'auth.supabase.tokenExpired' },
  { pattern: /not found/i, translationKey: 'auth.supabase.resourceNotFound' },
  { pattern: /already exists/i, translationKey: 'auth.supabase.resourceAlreadyExists' },
];

export const translateSupabaseError = (
  error: AuthError | PostgrestError | any, 
  t: (key: string) => string
): string => {
  if (!error) {
    return t('auth.supabase.genericError');
  }

  const errorMessage = error.message || error.msg || String(error);
  
  // First, try direct mapping
  const directMapping = ERROR_MAPPINGS[errorMessage];
  if (directMapping && t(directMapping) !== directMapping) {
    return t(directMapping);
  }

  // Then, try pattern matching
  for (const { pattern, translationKey } of ERROR_PATTERNS) {
    if (pattern.test(errorMessage)) {
      const translated = t(translationKey);
      if (translated !== translationKey) {
        return translated;
      }
    }
  }

  // Check for specific error codes
  if (error.code) {
    const codeKey = `auth.supabase.code_${error.code}`;
    const translated = t(codeKey);
    if (translated !== codeKey) {
      return translated;
    }
  }

  // Fallback: try to use existing auth error keys
  const fallbackKey = 'auth.supabase.genericError';
  const fallback = t(fallbackKey);
  
  // If even the fallback key doesn't exist, return a basic translated error
  if (fallback === fallbackKey) {
    return t('auth.unexpectedError') || 'An unexpected error occurred';
  }

  return fallback;
};

export const createSupabaseErrorTranslator = (t: (key: string) => string): SupabaseErrorTranslator => ({
  translateSupabaseError: (error: AuthError | PostgrestError | any) => translateSupabaseError(error, t)
});
